import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { ICONS } from '@/components/icons/icons'

import styles from './InfoPopover.module.css'

interface InfoPopoverProps {
  /** Accessible name of the trigger, e.g. "How FPA picks the budget". */
  label: string
  /** Heading inside the popover (defaults to `label`). */
  title?: string
  /** Visible text next to the icon; icon-only when omitted. */
  triggerText?: string
  children: ReactNode
}

const GAP = 8
const MARGIN = 12

function overlayRoot(): HTMLElement {
  return document.getElementById('overlay-root') ?? document.body
}

/**
 * A small "?" / info disclosure for contextual help: click (or Enter/Space) toggles a non-modal
 * popover rendered through a portal and anchored under the trigger; Escape, a click outside or
 * scrolling the page away closes it and focus returns to the trigger.
 */
export function InfoPopover({ label, title, triggerText, children }: InfoPopoverProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const id = useId()

  const toggle = useCallback(() => setOpen((v) => !v), [])
  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  // Anchor under the trigger, kept inside the viewport (geometry set through CSS custom properties).
  useLayoutEffect(() => {
    if (!open) return undefined
    const place = () => {
      const trigger = triggerRef.current
      const pop = popRef.current
      if (!trigger || !pop) return
      const r = trigger.getBoundingClientRect()
      const width = pop.offsetWidth
      const left = Math.min(Math.max(MARGIN, r.left), window.innerWidth - width - MARGIN)
      pop.style.setProperty('--pop-top', `${Math.round(r.bottom + GAP)}px`)
      pop.style.setProperty('--pop-left', `${Math.round(Math.max(MARGIN, left))}px`)
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  const handlePopKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
    },
    [close],
  )

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`${styles.trigger} ${triggerText ? styles.withText : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={triggerText ? undefined : label}
      >
        <ICONS.info size={14} />
        {triggerText ? <span>{triggerText}</span> : null}
      </button>
      {open
        ? createPortal(
            <div
              id={id}
              ref={popRef}
              className={styles.popover}
              role="dialog"
              aria-label={title ?? label}
              onKeyDown={handlePopKeyDown}
            >
              <div className={styles.head}>
                <h3 className={styles.title}>{title ?? label}</h3>
                <button type="button" className={styles.close} onClick={close} aria-label="Close help">
                  <ICONS.close size={14} />
                </button>
              </div>
              <div className={styles.body}>{children}</div>
            </div>,
            overlayRoot(),
          )
        : null}
    </>
  )
}
